import {defineStore} from 'pinia';
import {ref, computed} from 'vue';
import {WalkthroughStep} from '@core/types/walkthrough';
import {getWalkthrough} from '@walkthroughs/index';
import WalkthroughService from '@core/services/WalkthroughService';
import ConfigProvider from '@core/services/ConfigProvider';
import {resolveWalkthroughNavRouter} from '@core/store/walkthroughNavRouter';

export const useWalkthroughStore = defineStore(
  'WalkthroughStore',
  () => {
    const currentWalkthroughId = ref<string | null>(null);
    const currentStepIndex = ref<number>(-1);
    const seenWalkthroughs = ref<string[]>([]);
    const isModalOpen = ref(false);
    const isActive = ref(false);
    const isLoading = ref(false);

    const isEnabled = computed(() => {
      const enabled = ConfigProvider.value('walkthroughEnabled');
      return enabled === true || enabled === 'true';
    });

    const currentWalkthrough = computed(() =>
      currentWalkthroughId.value && isEnabled.value
        ? getWalkthrough(currentWalkthroughId.value)
        : null,
    );

    const currentStep = computed<WalkthroughStep | null>(() => {
      if (!currentWalkthrough.value || currentStepIndex.value === -1)
        return null;
      return currentWalkthrough.value.steps[currentStepIndex.value] || null;
    });

    const isSeen = (id: string) => seenWalkthroughs.value.includes(id);

    async function loadProgress() {
      if (!isEnabled.value) return;
      try {
        isLoading.value = true;
        const allProgress = await WalkthroughService.getAllProgress();
        seenWalkthroughs.value = allProgress.map(p => p.walkthroughId);
      } catch (error) {
        console.error('Failed to load walkthrough progress:', error);
      } finally {
        isLoading.value = false;
      }
    }

    async function startWalkthrough(id: string, forceRestart = false) {
      if (!isEnabled.value) return;
      const walkthrough = getWalkthrough(id);
      if (walkthrough) {
        currentWalkthroughId.value = id;
        isModalOpen.value = true;
        isActive.value = false;

        if (forceRestart) {
          currentStepIndex.value = -1;
          return;
        }

        try {
          isLoading.value = true;
          const progress = await WalkthroughService.getProgress(id);
          currentStepIndex.value =
            progress &&
            progress.status !== 'completed' &&
            progress.status !== 'skipped' &&
            progress.status !== 'seen_intro'
              ? progress.currentStep
              : -1;

          if (!progress) {
            await WalkthroughService.saveProgress({
              walkthroughId: id,
              currentStep: -1,
              status: 'seen_intro',
            });
          }
          if (!isSeen(id)) {
            seenWalkthroughs.value.push(id);
          }
        } catch (error) {
          console.error('Failed to fetch walkthrough progress:', error);
          currentStepIndex.value = -1;
        } finally {
          isLoading.value = false;
        }
      }
    }

    async function continueToWalkthrough() {
      const navRouter = resolveWalkthroughNavRouter();
      isModalOpen.value = false;
      isActive.value = true;

      if (currentStepIndex.value === -1) {
        currentStepIndex.value = 0;
        await saveProgress();
      }

      if (currentStep.value?.route && navRouter) {
        await navRouter.push(currentStep.value.route);
      }
    }

    async function nextStep() {
      const navRouter = resolveWalkthroughNavRouter();
      if (!currentWalkthrough.value) return;

      if (currentStepIndex.value < currentWalkthrough.value.steps.length - 1) {
        currentStepIndex.value++;
        await saveProgress();

        if (currentStep.value?.route && navRouter) {
          await navRouter.push(currentStep.value.route);
        }
      } else {
        await completeWalkthrough();
      }
    }

    async function saveProgress() {
      if (!currentWalkthroughId.value) return;

      try {
        await WalkthroughService.saveProgress({
          walkthroughId: currentWalkthroughId.value,
          currentStep: currentStepIndex.value,
          status: 'in_progress',
        });
      } catch (error) {
        console.error('Failed to save walkthrough progress:', error);
      }
    }

    async function completeWalkthrough() {
      if (currentWalkthroughId.value) {
        try {
          await WalkthroughService.saveProgress({
            walkthroughId: currentWalkthroughId.value,
            currentStep: currentStepIndex.value,
            status: 'completed',
          });

          if (!isSeen(currentWalkthroughId.value)) {
            seenWalkthroughs.value.push(currentWalkthroughId.value);
          }
        } catch (error) {
          console.error('Failed to complete walkthrough on backend:', error);
        }
      }
      stopWalkthrough();
    }

    async function stopWalkthrough(skipped = false) {
      if (currentWalkthroughId.value && skipped) {
        try {
          await WalkthroughService.saveProgress({
            walkthroughId: currentWalkthroughId.value,
            currentStep: currentStepIndex.value,
            status: 'skipped',
          });

          if (!isSeen(currentWalkthroughId.value)) {
            seenWalkthroughs.value.push(currentWalkthroughId.value);
          }
        } catch (error) {
          console.error('Failed to skip walkthrough on backend:', error);
        }
      }
      currentWalkthroughId.value = null;
      currentStepIndex.value = -1;
      isModalOpen.value = false;
      isActive.value = false;
    }

    return {
      currentWalkthroughId,
      currentStepIndex,
      seenWalkthroughs,
      isModalOpen,
      isActive,
      isLoading,
      currentWalkthrough,
      currentStep,
      isSeen,
      loadProgress,
      startWalkthrough,
      continueToWalkthrough,
      nextStep,
      completeWalkthrough,
      stopWalkthrough,
      isEnabled,
    };
  },
  {
    persist: {
      pick: [
        'currentWalkthroughId',
        'currentStepIndex',
        'seenWalkthroughs',
        'isModalOpen',
        'isActive',
        'isLoading',
      ],
    },
  },
);
